import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import SetupProgressTracker from "./SetupProgressTracker";

import {
  Home,
  Users,
  Sprout,
  Megaphone,
  HeartPulse,
  LineChart,
  Settings,
  LogOut,
  ShoppingCart,
  Command,
  CreditCard
} from "lucide-react";
import { apiClient } from "../api/api";

const Sidebar = (props) => {
  const { logout, user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [profile, setProfile] = useState({
    name: "",
    role: "Brand Admin",
    photoUrl: null,
    companyType: user?.company_type || "" // Initialize from context to prevent flicker
  });
  const [brandName, setBrandName] = useState(() => {
    if (user && (user.shop_name || user.brand_name)) {
      return user.shop_name || user.brand_name;
    }
    return "";
  });
  const [photoError, setPhotoError] = useState(false);
  const { sidebarOpen = false, setSidebarOpen, isDesktop = false } = props;
  const location = useLocation();

  const fetchUserProfile = async () => {
    try {
      const resp = await apiClient.get('/user/profile', { withCredentials: true });
      if (resp.data?.user) {
        const u = resp.data.user;
        setProfile({
          name: u.name || u.username || "User",
          role: u.role || "Admin",
          photoUrl: u.profile_photo_url,
          companyType: u.company_type
        });
      }
    } catch (err) {
      console.error("[Sidebar] Error fetching profile:", err);
    }
  };

  React.useEffect(() => {
    fetchUserProfile();
    if (user && (user.shop_name || user.brand_name)) {
      setBrandName(user.shop_name || user.brand_name);
      return;
    }
    const fetchBrandName = async () => {
      try {
        const resp = await apiClient.get(`/get_brand_name`, {
          credentials: "include",
          withCredentials: true,
        });
        setBrandName(resp.data?.name || user?.email || "My Shop");
      } catch (err) {
        console.error("[Sidebar] Error fetching brand name:", err);
        setBrandName(user?.email || "My Shop");
      }
    };
    if (user && user.brand_id) {
      fetchBrandName();
    }
  }, [user]);

  const handleNavClick = () => {
    if (!isDesktop && setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const styles = {
    sidebar: {
      width: "100%",
      maxWidth: "256px",
      backgroundColor: isLight ? "#FFFFFF" : "#050505",
      borderRight: isLight ? "1px solid #E5E7EB" : "none",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      fontFamily: "system-ui, -apple-system, sans-serif",
      position: "relative",
      zIndex: 1000,
      overflowY: "auto",
      color: isLight ? "#111827" : "#ffffff",
      padding: "16px",
    },
    headerSection: {
      padding: "5px 8px",
      marginBottom: "24px",
    },
    headerTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    },
    logoText: {
      display: 'flex',
      alignItems: 'baseline',
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      fontSize: '26px',
      fontWeight: '800',
      letterSpacing: '-0.5px',
      lineHeight: 1,
      gap: 0,
    },
    logoShop: {
      color: isLight ? '#111827' : '#FFFFFF',
    },
    logoOS: {
      background: 'linear-gradient(135deg, #E85478 0%, #C83A6B 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    logoDot: {
      color: isLight ? '#374151' : '#FFFFFF',
    },
    headerTitleSpan: {
      color: "#fff",
      background: "linear-gradient(to right, #ff6b8b, #ff9a8b)",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    nav: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      padding: "12px 16px",
      fontSize: "15px",
      fontWeight: "500",
      borderRadius: "12px",
      textDecoration: "none",
      transition: "all 0.2s ease",
      cursor: "pointer",
      width: "100%",
      marginBottom: "4px",
    },
    navItemActive: isLight ? {
      background: "rgba(153, 64, 89, 0.14)",
      color: "#7B2D44",
      borderTop: "1px solid rgba(153, 64, 89, 0.22)",
      borderRight: "1px solid rgba(153, 64, 89, 0.22)",
      borderBottom: "1px solid rgba(153, 64, 89, 0.22)",
      borderLeft: "3px solid rgba(153, 64, 89, 0.85)",
      paddingLeft: "13px",
      boxShadow: "0 1px 8px rgba(153, 64, 89, 0.10)",
    } : {
      background: "linear-gradient(90deg, rgba(153, 64, 89, 0.4) 0%, rgba(153, 64, 89, 0.06) 100%)",
      color: "#ffffff",
      borderTop: "1px solid rgba(153, 64, 89, 0.25)",
      borderRight: "1px solid rgba(153, 64, 89, 0.25)",
      borderBottom: "1px solid rgba(153, 64, 89, 0.25)",
      borderLeft: "3px solid rgba(200, 68, 108, 0.92)",
      paddingLeft: "14px",
      boxShadow: "0 2px 20px rgba(153, 64, 89, 0.18)",
    },
    navItemInactive: {
      color: isLight ? "#4B5563" : "#9ca3af",
      backgroundColor: "transparent",
      border: "1px solid transparent",
    },
    navIcon: {
      width: "20px",
      height: "20px",
      marginRight: "16px",
    },
    bottomSection: {
      marginTop: "auto",
      borderTop: isLight ? "1px solid #E5E7EB" : "1px solid #1f2937",
      paddingTop: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    userProfile: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      marginTop: "8px",
      borderRadius: "12px",
      // backgroundColor: "#1f2937",
    },
    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: isLight ? "#E5E7EB" : "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    userInfo: {
      display: "flex",
      flexDirection: "column",
    },
    userName: {
      fontSize: "14px",
      fontWeight: "600",
      color: isLight ? "#111827" : "#ffffff",
    },
    userRole: {
      fontSize: "12px",
      color: isLight ? "#6B7280" : "#9ca3af",
    },
  };

  const navigationItems = [
    {
      id: "home",
      name: "Home",
      href: "/dashboard",
      icon: Home,
    },
    ...(profile.companyType === "Agency" || profile.role === "cs_internal" ? [{
      id: "command-centre",
      name: "Command Center",
      href: "/command-centre",
      icon: Command,
    }] : []),
    {
      id: "affiliates",
      name: "Affiliates",
      href: "/affiliates",
      icon: Users,
    },
    {
      id: "organic",
      name: "Organic",
      href: "/organic",
      icon: Sprout,
    },
    {
      id: "paid-ads",
      name: "Paid Ads",
      href: "/paid-ads",
      icon: Megaphone,
    },
    {
      id: "shop-health",
      name: "Shop Health",
      href: "/shop-health",
      icon: HeartPulse,
    },
    {
      id: "pnl",
      name: "P&L",
      href: "/pnl",
      icon: LineChart,
    },
    // My Shops is an agency-only shop-switcher -- a client_viewer login is permanently
    // scoped to its own one shop (2026-09-16), so switching shops makes no sense to show.
    ...(profile.role === "client_viewer" ? [] : [{
      id: "myshops",
      name: "My Shops",
      href: "/myshops",
      icon: ShoppingCart,
    }]),
    {
      id: "subscription",
      name: "Subscription",
      href: "/subscription",
      icon: CreditCard,
    },
  ];

  const handleNavItemHover = (e) => {
    if (!e.currentTarget.closest('[data-active="true"]')) {
      e.currentTarget.style.color = isLight ? "#111827" : "#ffffff";
      if (isLight) e.currentTarget.style.backgroundColor = "#F3F4F6";
    }
  };

  const handleNavItemLeave = (e) => {
    if (!e.currentTarget.closest('[data-active="true"]')) {
      e.currentTarget.style.color = isLight ? "#4B5563" : "#9ca3af";
      if (isLight) e.currentTarget.style.backgroundColor = "transparent";
    }
  };

  return (
    <div style={styles.sidebar}>
      {/* Mobile Close Button */}
      {!isDesktop && sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2002,
            background: "transparent",
            border: "none",
            color: "white",
            fontSize: 24,
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      )}

      {/* Header */}
      <div style={styles.headerSection}>
        <div style={styles.headerTitle}>
          <span style={styles.logoText}>
            <span style={styles.logoShop}>shop</span>
            <span style={styles.logoOS}>OS</span>
            <span style={styles.logoDot}>.</span>
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.id}
              to={item.href}
              style={{
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : styles.navItemInactive),
              }}
              data-active={isActive}
              onMouseEnter={handleNavItemHover}
              onMouseLeave={handleNavItemLeave}
              onClick={handleNavClick}
            >
              <Icon style={styles.navIcon} />
              {item.name}
            </Link>
          );
        })}
      </nav>
      {/* SetupProgressTracker hidden — not yet functional */}
      {/* <SetupProgressTracker /> */}

      {/* Bottom Section */}
      <div style={styles.bottomSection}>
        {/* Setup Progress Tracker */}
        {/* <SetupProgressTracker /> */}
        
        <Link
          to="/settings"
          style={{
            ...styles.navItem,
            ...(location.pathname === "/settings"
              ? styles.navItemActive
              : styles.navItemInactive),
          }}
          onClick={handleNavClick}
        >
          <Settings style={styles.navIcon} />
          Settings
        </Link>
        <button
          onClick={logout}
          style={{
            ...styles.navItem,
            ...styles.navItemInactive,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <LogOut style={styles.navIcon} />
          Logout
        </button>
        <div style={styles.userProfile}>
          <div style={styles.avatar}>
            {profile.photoUrl && !photoError ? (
              <img
                src={profile.photoUrl}
                alt="User"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setPhotoError(true)}
              />
            ) : (
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#ff6b8b' }}>
                {profile.name ? profile.name[0].toUpperCase() : 'U'}
              </span>
            )}
          </div>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{profile.name || "User"}</span>
            <span style={styles.userRole}>{profile.role}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
